import type { Overmind } from 'overmind';

import * as CalculationModule from '@/features/execute-calculation-set/overmind/CalculationModule';
import type { TacticResultBrowsingFilter } from '@/features/execute-calculation-set/overmind/calculation/actions';
import type { CalculationState } from '@/features/execute-calculation-set/overmind/calculation/createCalculationState';
import { findFeature } from '@/features/execute-calculation-set/overmind/calculation/derived-state/gridElementsDetailsByCalculationType/findFeature';
import type {
  ArcgisMapAdapter,
  ArcgisMapServerConfig
} from '@/overmind/effects/adapters/ArcgisMapAdapter/ArcgisMapAdapter';

import type { Context } from '../..';
import { GUID } from '@/overmind/effects/auth';

export function setupReactions(overmindInstance: Overmind<Context>) {
  handleAvailableTenants(overmindInstance);
  handleCheckingArcGisUserAlreadySignedIn(overmindInstance);
  fetchArcGisToken(overmindInstance);
  onTenantSelected(overmindInstance);
  createArcgisAdapterOnConfig(overmindInstance);
  setArcgisTokenOnArcgisAdapter(overmindInstance);
  handleTacticResultBrowsingFilterReaction(overmindInstance);
  onStoppedBrowsingCalculationTacticResults(overmindInstance);
  onFeatureSelection(overmindInstance);
  setupFeatureTogglesReader(overmindInstance);
  setupConfigurationAccessCheck(overmindInstance);
  handleCalculationSetExecution(overmindInstance);
  onNoAvailableTenants(overmindInstance);
  handleAuthorizationWhenCalculationSetExecutedExternally(overmindInstance);
  initializeGettingGridElementDetailsEffect(overmindInstance);
  CalculationModule.setupReactions(overmindInstance);
  onFeatureSelected(overmindInstance);
}

export function onFeatureSelected(context: Context) {
  context.reaction(
    state =>
      [
        state.selectedFeature.current,
        context.state.calculation.externallyTriggeredCalculationSetExecution
      ] as const,
    async ([selectedFeature, externallyTriggeredCalculationSetExecution]) => {
      if (
        selectedFeature &&
        externallyTriggeredCalculationSetExecution.current !== 'READY'
      ) {
        await context.actions.selectedFeature.setCalculationSets();
      }
    }
  );
}

export function onTenantSelected(context: Context) {
  context.reaction(
    state =>
      [
        state.tenants.currentTenant,
        state.tenants.hasAccessToApplication,
        state.auth
      ] as const,
    async ([currentTenant, hasAccessToApplication, auth]) => {
      context.effects.log.info(
        'onTenantSelected',
        currentTenant,
        hasAccessToApplication,
        auth
      );
      if (currentTenant.type === 'NO_TENANT_SELECTED') return;

      if (
        currentTenant.type === 'SELECTED_TENANT' &&
        hasAccessToApplication.type === 'NOT_CHECKED'
      ) {
        await context.actions.tenants.checkApplicationAccess(
          currentTenant.context.tenantId
        );
      }
      if (
        currentTenant.type === 'SELECTED_TENANT' &&
        hasAccessToApplication.type === 'HAS_ACCESS'
      ) {
        if (context.state.arcgis.config.current !== 'LOADED') {
          await context.actions.arcgis.config.loadMapConfiguration();
          await context.actions.calculation.loadCalculationSets();
        }
      }
      if (hasAccessToApplication.type === 'NO_ACCESS') {
        context.actions.showUnauthorized();
      }
    }
  );
}

export function onNoAvailableTenants(context: Context) {
  context.reaction(
    state => [state.tenants.availableTenants.type] as const,
    ([availableTenants]) => {
      if (availableTenants === 'NOT_AVAILABLE') {
        context.actions.showUnauthorized();
      }
    }
  );
}

export function handleAvailableTenants(instance: Context) {
  instance.reaction(
    ({ auth, config }) => [auth, config.type] as const,
    async ([auth, config]) => {
      if (
        auth.current === 'AWAITING_FEATURE_TOGGLES' &&
        config === 'AVAILABLE'
      ) {
        instance.actions.tenants.loadAvailableTenants();
      }
    }
  );
}

export function fetchArcGisToken({ reaction, actions }: Context) {
  reaction(
    ({ tenants, auth, config, arcgis, featureToggles }) =>
      [
        tenants.currentTenant,
        auth,
        config.type,
        arcgis.config.value,
        featureToggles
      ] as const,
    ([currentTenant, auth, config, arcgisConfig, featureToggles]) => {
      actions.handleArcGisToken({
        currentTenant,
        auth,
        config,
        arcgisConfig,
        featureToggles
      });
    }
  );
}

export function createArcgisAdapterOnConfig({
  reaction,
  effects,
  actions
}: Context) {
  reaction(
    state => [state.arcgis.config.value, state.featureToggles?.shouldUseArcGisTokenRefreshing] as const,
    ([configValue, shouldUseArcGisTokenRefreshing]) => {
      if (configValue) {
        const providedConfig = configValue.mapSource;
        const adapterConfig: ArcgisMapServerConfig = {
          baseUrl: providedConfig.url,
          domainIdField: providedConfig.domainIdField,
          arcgisIdField: providedConfig.arcgisIdField,
          typeField: providedConfig.typeField,
          titleField: providedConfig.titleField,
          precision: 8,
          searchRules: providedConfig.searchRules
        };
        const adapterOptions = {
          onConfiguredToken: () => actions.arcGisTokenReady(),
          shouldUseTokenRefreshing: () => Boolean(shouldUseArcGisTokenRefreshing),
          routeToRedirectUri: effects.arcgis.auth.identity.routeToRedirectUri,
          portalUrl: configValue.map.webMap?.url,
          sharingUrl: configValue.map.webMap?.sharingUrl ?? `${configValue.map.webMap?.url}/sharing`,
          restServicesUrl: configValue.map.webMap?.restServicesUrl ?? `${configValue.map.webMap?.url}/rest/services`
        };
        effects.arcgisMapAdapter.initializeAdapter(adapterConfig, adapterOptions);
      }
    }
  );
}

export const initializeGettingGridElementDetailsEffect = (context: Context) =>
  context.reaction(
    state => [state.arcgis.config.value, state.auth] as const,
    ([arcgisConfig, auth]) => {
      if (arcgisConfig && auth.current === 'AUTHENTICATED') {
        const adapter = context.effects.arcgisMapAdapter.getAdapter();
        if (!adapter) return;
        context.effects.api.gridElementDetails.initialize({
          getGridElementDetails: () =>
            adapter.getLayerDataElements(arcgisConfig.mapSource.layerIds),
          findFeature: objectId => {
            const features =
              context.state.arcgis.featureGeometries.current === 'LOADED'
                ? context.state.arcgis.featureGeometries.value.flatMap(
                    layer => layer.features
                  )
                : [];

            return findFeature(features, objectId);
          }
        });
      }
    }
  );

export const handleArcGisTokenChange =
  (
    getAdapter: () => ArcgisMapAdapter | undefined,
    loadFeatureServer: () => Promise<void>,
    loadFeatureTypes: () => Promise<void>
  ) =>
  async ([auth, arcgisConfig]: [any, any]) => {
    const adapter = getAdapter();
    if (!adapter) {
      return;
    }
    if (auth.current === 'AUTHENTICATED' && arcgisConfig.current === 'LOADED') {
      await adapter.setToken(auth.arcgisToken);
    } else {
      adapter.resetToken();
      return;
    }
    await Promise.all([loadFeatureServer(), loadFeatureTypes()]);
  };

export function setArcgisTokenOnArcgisAdapter(ctx: Context) {
  ctx.reaction(
    state => [state.auth, state.arcgis.config] as any,
    handleArcGisTokenChange(
      ctx.effects.arcgisMapAdapter.getAdapter,
      ctx.actions.arcgis.featureServer.loadFeatureServer,
      ctx.actions.arcgis.featureTypes.loadFeatureTypes
    )
  );
}

export function handleCheckingArcGisUserAlreadySignedIn(ctx: Context) {
  ctx.reaction(
    state => [state.auth, state.arcgis.config, state.featureToggles] as const,
    async ([auth, config, featureToggles]) => {
      if (
        auth.current === 'AUTHENTICATING_ARCGIS' &&
        !auth.redirecting &&
        config.current === 'LOADED' &&
        config.value.esriId &&
        featureToggles?.isNewAuthVersionEnabled
      ) {
        await ctx.actions.arcgis.token.handleAlreadySignedInArcGisUser(
          config.value
        );
      }
    }
  );
}

export function handleAuthorizationWhenCalculationSetExecutedExternally(
  ctx: Context
) {
  ctx.reaction(
    state =>
      [
        state.calculation.externallyTriggeredCalculationSetExecution,
        state.auth
      ] as const,
    async ([externallyTriggeredCalculationSetExecution, auth]) => {
      if (
        externallyTriggeredCalculationSetExecution.current !== 'IDLE' &&
        auth.current === 'AWAITING_ARCGIS_AUTHENTICATION'
      ) {
        ctx.actions.calculation.completeAuthenticationWithExternalArcGisToken(
          externallyTriggeredCalculationSetExecution.calculationContext
            .arcgisToken
        );
      }
    }
  );
}

export function setupFeatureTogglesReader(ctx: Context) {
  ctx.reaction(
    state => [state.auth, state.tenants.currentTenant, state.config] as const,
    async ([auth, currentTenant, appConfig]) => {
      if (
        auth.current === 'FEATURE_TOGGLES_IN_PROGRESS' ||
        currentTenant.type === 'NO_TENANT_SELECTED'
      )
        return;
      if (
        auth.current === 'AWAITING_FEATURE_TOGGLES' &&
        currentTenant.type === 'SELECTED_TENANT'
      ) {
        if (appConfig.type === 'NOT_AVAILABLE') return;
        await ctx.effects.featureToggleReader.startFor(
          appConfig.config.unleash,
          auth.user.identityProvider,
          auth.user.preferredUsername,
          currentTenant.context.tenantId,
          () => {
            const isEsriWebMapEnabled = ctx.effects.featureToggleReader.read(
              'anb-grid-calculator-esri-web-map'
            );
            const isSharingExecutionEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-sharing-execution'
              );
            const isSaveErrorReportEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-saving-error-report'
              );
            const isShowingGridElementTitleEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-web-map-title'
              );
            const isShowingApplicationVersionEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-showing-application-version'
              );
            const isContainmentExecutionTimeCalculatedEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-containment-execution-time-calculated'
              );
            const isNewAuthVersionEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-named-users'
              );
            const isProtectiveDevicesEnabled =
              ctx.effects.featureToggleReader.read('protective-devices');

            const isWebmapBackgroundMapEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-arcgis-background-map-for-leaflet'
              );
            const isNewPropertiesForAcLineSegmentEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-new-propeties-for-aclinesegment-frontend'
              );

            const isArcGisJsSDKWebMapForBrowsingCalculationResultsEnabled =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-calculation-results-esri-web-map'
              );

            const shouldUseArcGisTokenRefreshing = 
              ctx.effects.featureToggleReader.read('pg-anb-grid-calculator-arcgis-token-refreshing')
            
            
              const shouldUseInitiatingObjectHighlighting = 
              ctx.effects.featureToggleReader.read('pg-anb-grid-calculator-calculation-results-initating-object-highlighting')

              const evaluationRulesTechnicalBoundariesAdjustment =
              ctx.effects.featureToggleReader.read(
                'pg-anb-grid-calculator-calculation-results-technical-boundaries-adjustment'
              );

            ctx.actions.internal.setFeatureToggles({
              isEsriWebMapEnabled,
              isSharingExecutionEnabled,
              isSavingCalculationSetExecutionReportEnabled:
                isSaveErrorReportEnabled,
              isShowingGridElementTitleEnabled,
              isShowingApplicationVersionEnabled,
              isContainmentExecutionTimeCalculatedEnabled,
              isNewAuthVersionEnabled,
              isProtectiveDevicesEnabled,
              isWebmapBackgroundMapEnabled,
              isNewPropertiesForAcLineSegmentEnabled,
              isArcGisJsSDKWebMapForBrowsingCalculationResultsEnabled,
              shouldUseArcGisTokenRefreshing,
              shouldUseInitiatingObjectHighlighting,
              evaluationRulesTechnicalBoundariesAdjustment
            });
          },
          () => ctx.actions.internal.resetFeatureToggles()
        );
      }
    }
  );
}

export function setupConfigurationAccessCheck(ctx: Context) {
  let checkedForTenant: string | null = null;
  ctx.reaction(
    state =>
      [state.auth, state.tenants.currentTenant, state.config] as const,
    async ([auth, currentTenant, appConfig]) => {
      if (currentTenant.type === 'NO_TENANT_SELECTED') return;

      const tenantId = currentTenant.context.tenantId;
      if (checkedForTenant === tenantId) return;

      const hasUser =
        auth.current === 'AUTHENTICATED' ||
        auth.current === 'AWAITING_ARCGIS_AUTHENTICATION';

      if (!hasUser || appConfig.type === 'NOT_AVAILABLE') return;

      const token = (auth as { user: { token: string } }).user?.token;
      const configApiUrl = appConfig.config.api.configApiUrl;

      if (!token || !configApiUrl) return;

      checkedForTenant = tenantId;

      try {
        const response = await fetch(
          `${configApiUrl}api/v1/${tenantId}/configurations/webApp`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        ctx.actions.internal.setConfigApiAccess(response.ok || response.status === 404);
      } catch {
        ctx.actions.internal.setConfigApiAccess(false);
      }
    }
  );
}

export function handleTacticResultBrowsingFilterReaction(
  overmindInstance: Context
) {
  overmindInstance.reaction(
    (state: Context['state']) =>
      [
        state.calculation.availableCalculationScenarios,
        state.calculation.tacticResultBrowsingFilter
        //   ,state.calculation.calculationTactic
      ] as const,
    handleTacticResultBrowsingFilter(
      overmindInstance.actions.calculation.browseTacticResults
    )
  );
}
export const handleTacticResultBrowsingFilter =
  (action: (tacticResultBrowsingFilter: TacticResultBrowsingFilter) => void) =>
  ([
    availableCalculationScenarios,
    tacticResultBrowsingFilter
  ]: // , calculationTactic
  readonly [
    CalculationState['availableCalculationScenarios'],
    CalculationState['tacticResultBrowsingFilter']
  ]) => {
    if (tacticResultBrowsingFilter.type !== 'NONE_AVAILABLE') return;
    if (availableCalculationScenarios.type === 'ALL_AVAILABLE') {
      action({ type: 'ALL' });
      return;
    }
    if (
      availableCalculationScenarios.type === 'SOME_AVAILABLE' &&
      availableCalculationScenarios.scenarios.has('SHORT_CIRCUIT')
    ) {
      action({ type: 'SHORT_CIRCUIT' });
      return;
    }
    if (
      availableCalculationScenarios.type === 'SOME_AVAILABLE' &&
      availableCalculationScenarios.scenarios.has('LOAD_FLOW')
    ) {
      action({ type: 'ALL_LOAD_FLOW' });
    }
  };

export function onStoppedBrowsingCalculationTacticResults(
  overmindInstance: Context
) {
  overmindInstance.reaction(
    state => [state.calculation.calculationTactic.current] as const,
    handleStoppedBrowsingCalculationTacticResults(
      overmindInstance.actions.arcgis.featureGeometries
        .resetFeatureGeometriesState
    )
  );
}

export const handleStoppedBrowsingCalculationTacticResults =
  (
    resetFeatureGeometriesState: Context['actions']['arcgis']['featureGeometries']['resetFeatureGeometriesState']
  ) =>
  ([calculationTacticState]: readonly [
    'NOT_LOADED' | 'LOADING' | 'ERROR' | 'LOADED'
  ]) => {
    if (calculationTacticState === 'NOT_LOADED') {
      resetFeatureGeometriesState();
    }
  };

export function onFeatureSelection(overmindInstance: Context) {
  overmindInstance.reaction(
    state =>
      [
        state.selectedFeature.current,
        state.calculation.externallyTriggeredCalculationSetExecution
      ] as const,
    async ([selectedFeature, externallyTriggeredCalculationSetExecution]) => {
      if (externallyTriggeredCalculationSetExecution.current !== 'READY') {
        overmindInstance.actions.calculation.unloadCalculation();
        overmindInstance.actions.calculation.hideNotification();
      }
    }
  );
}

function handleCalculationSetExecution(overmindInstance: Context) {
  overmindInstance.reaction(
    state =>
      [
        state.auth.current,
        state.calculation.externallyTriggeredCalculationSetExecution.current
      ] as const,
    ([auth, externalCalculation]) => {
      if (
        auth === 'AUTHENTICATED' ||
        calculationStartedAfterAuthentication(auth, externalCalculation)
      ) {
        overmindInstance.actions.calculation.externallyTriggeredCalculationSetExecutionAuthenticated();
      }
    }
  );

  overmindInstance.reaction(
    state => [state.tenants.availableTenants] as const,
    ([availableTenants]) => {
      if (availableTenants.type === 'AVAILABLE') {
        overmindInstance.actions.calculation.externallyTriggeredCalculationSetExecutionTenantsAvailable();
      }
    }
  );

  overmindInstance.reaction(
    state =>
      [
        state.calculation.externallyTriggeredCalculationSetExecution,
        state.tenants.hasAccessToApplication
      ] as const,
    ([calculationSetExecution, hasAccessToApplication]) => {
      if (
        hasAccessToApplication.type !== 'NOT_CHECKED' &&
        calculationSetExecution.current ===
          'AWAITING_APPLICATION_ACCESS_CONFIRMED'
      ) {
        overmindInstance.actions.calculation.externallyTriggeredCalculationSetExecutionAccessChecked(
          hasAccessToApplication
        );
      }
    }
  );

  overmindInstance.reaction(
    state =>
      [
        state.calculation.externallyTriggeredCalculationSetExecution,
        state.calculation.calculationTactic
      ] as const,
    async ([calculationSetExecution, calculationSetExecutionResults]) => {
      if (
        calculationSetExecutionResults.current === 'LOADED' ||
        calculationSetExecutionResults.current === 'ERROR'
      )
        return;
      if (calculationSetExecution.current === 'READY') {
        await overmindInstance.actions.calculation.getCalculationSetEvaluationRules({
          tenantId: calculationSetExecution.calculationContext.tenantId,
        });
        await overmindInstance.actions.calculation.triggerCalculationSetExecution(
          Object.assign({}, calculationSetExecution.calculationContext)
        );
      }
    }
  );
}
function calculationStartedAfterAuthentication(
  auth: string,
  externalCalculation: string
): boolean {
  return auth === 'AUTHENTICATED' && externalCalculation === 'AWAITING';
}
